import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface FavoritoCategoria {
  name: string;
  count: number;
  percentage: number;
}

interface FavoritosData {
  favoritos_por_categoria?: FavoritoCategoria[];
}

interface BarChartProps {
  data?: FavoritosData;
  style?: React.CSSProperties; // ✅ adicionando suporte a style como prop
}

export const FavoritosChart: React.FC<BarChartProps> = ({ data, style }) => {
  const categorias = data?.favoritos_por_categoria ?? [];
  const isLoading = !data?.favoritos_por_categoria;

  // Cores por valor
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getColor = (value: number) => {
    if (value >= 80) return '#2A2C85';
    if (value >= 60) return '#5C68F8';
    if (value >= 40) return '#A1B6FF';
    if (value >= 20) return '#EDF3FF';
    return '#F5F7FF';
  };

  const chartData = {
    labels: categorias.map((item) => item.name),
    datasets: [
      {
        label: 'Aprovação (%)',
        data: categorias.map((item) => item.percentage ?? 0),
        backgroundColor: categorias.map((item) => getColor(item.percentage ?? 0)),
        borderRadius: {
          topLeft: 0,
          topRight: 6,
          bottomLeft: 0,
          bottomRight: 6,
        },
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    indexAxis: 'y' as const, // horizontal
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            return `${context.raw}% de aprovação`;
          },
        },
      },
      datalabels: { display: false },
    },
    scales: {
      x: { display: false },
      y: { ticks: { display: true } },
    },
  } as const;

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '1rem',
        maxWidth: '804px',
        maxHeight: 500,
        minWidth: 304,
        width: '100%',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        ...style, // ✅ aplica style externo
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '1rem',
          alignItems: 'center',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, color: '#5A5A5A' }}>Categorias dos eventos</h3>
      </div>

      {/* Chart */}
      {isLoading ? (
        <div
          style={{
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
          }}
        >
          ...
        </div>
      ) : (
        <>
          <Bar data={chartData} options={options} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              marginTop: '0.5rem',
              gap: 10,
              justifyContent: 'space-between',
              marginLeft: 70,
            }}
          >
            {categorias
              .filter((c) => c.percentage > 0)
              .sort((a, b) => a.percentage - b.percentage)
              .map((c) => (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  key={c.name}
                >
                  <span
                    style={{ fontSize: 13, color: '#5A5A5A', marginBottom: 2, fontWeight: 500 }}
                  >
                    {c.percentage}%
                  </span>
                  <span
                    style={{ fontSize: 13, color: '#5A5A5A', marginBottom: 2, fontWeight: 500 }}
                  >
                    aprovação
                  </span>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
};
